// ===================================
// ADMIN ERROR LOGS PAGE
// src/pages/AdminErrors.js
// Stage 11: Full error log viewer with filtering
// ===================================

import React, { useState, useEffect, useCallback } from 'react';
import './AdminErrors.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const AdminErrors = () => {
  const [errors, setErrors] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState(null);
  const [filters, setFilters] = useState({
    severity: '',
    error_type: '',
    resolved: '',
    days: '7'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const fetchErrors = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await fetch(`${API_URL}/errors/admin?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch errors');

      const data = await response.json();
      setErrors(data.errors || []);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 0
      }));
    } catch (err) {
      console.error('Error fetching logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/errors/admin/stats?days=${filters.days}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [filters.days]);

  useEffect(() => {
    fetchErrors();
    fetchStats();
  }, [fetchErrors, fetchStats]);

  const handleResolve = async (errorId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/errors/admin/${errorId}/resolve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setErrors(prev => prev.map(e => 
        e.id === errorId ? { ...e, resolved: true, resolved_at: new Date().toISOString() } : e
      ));

      if (selectedError?.id === errorId) {
        setSelectedError(prev => ({ ...prev, resolved: true }));
      }
    } catch (err) {
      alert('Failed to resolve error');
    }
  };

  const viewErrorDetails = async (errorId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/errors/admin/${errorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch error details');

      const data = await response.json();
      setSelectedError(data.error);
    } catch (err) {
      console.error('Error fetching details:', err);
    }
  };

  const getSeverityClass = (severity) => {
    const classes = {
      critical: 'severity-critical',
      error: 'severity-error',
      warning: 'severity-warning',
      info: 'severity-info'
    };
    return classes[severity] || 'severity-info';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  const getSeverityIcon = (severity) => {
    const icons = { critical: '🔴', error: '🟠', warning: '🟡', info: '🔵' };
    return icons[severity] || '⚪';
  };

  if (loading && errors.length === 0) {
    return (
      <div className="admin-errors-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading error logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-errors-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>⚠️ Error Logs</h1>
          <p>Monitor and resolve application errors</p>
        </div>
        <button onClick={() => { fetchErrors(); fetchStats(); }} className="btn btn-secondary">
          🔄 Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="error-stats">
          <div className="stat-card critical">
            <div className="stat-icon">🔴</div>
            <div className="stat-content">
              <span className="stat-value">
                {stats.by_severity?.find(s => s.severity === 'critical')?.count || 0}
              </span>
              <span className="stat-label">Critical</span>
            </div>
          </div>
          <div className="stat-card error">
            <div className="stat-icon">🟠</div>
            <div className="stat-content">
              <span className="stat-value">
                {stats.by_severity?.find(s => s.severity === 'error')?.count || 0}
              </span>
              <span className="stat-label">Errors</span>
            </div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon">🟡</div>
            <div className="stat-content">
              <span className="stat-value">
                {stats.by_severity?.find(s => s.severity === 'warning')?.count || 0}
              </span>
              <span className="stat-label">Warnings</span>
            </div>
          </div>
          <div className="stat-card unresolved">
            <div className="stat-icon">📋</div>
            <div className="stat-content">
              <span className="stat-value">{stats.unresolved_count || 0}</span>
              <span className="stat-label">Unresolved</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <select 
          value={filters.severity} 
          onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
          className="filter-select"
        >
          <option value="">All Severities</option>
          <option value="critical">Critical</option>
          <option value="error">Error</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>

        <select 
          value={filters.resolved} 
          onChange={(e) => setFilters(prev => ({ ...prev, resolved: e.target.value }))}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="false">Unresolved</option>
          <option value="true">Resolved</option>
        </select>

        <select 
          value={filters.days} 
          onChange={(e) => setFilters(prev => ({ ...prev, days: e.target.value }))}
          className="filter-select"
        >
          <option value="1">Last 24 hours</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>

        <input
          type="text"
          placeholder="Filter by error type..."
          value={filters.error_type}
          onChange={(e) => setFilters(prev => ({ ...prev, error_type: e.target.value }))}
          className="filter-input"
        />
      </div>

      {/* Error List */}
      <div className="errors-container">
        <div className="errors-list">
          <table className="errors-table">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Type</th>
                <th>Message</th>
                <th>Page</th>
                <th>Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {errors.map(error => (
                <tr 
                  key={error.id} 
                  className={`error-row ${selectedError?.id === error.id ? 'selected' : ''} ${error.resolved ? 'resolved' : ''}`}
                  onClick={() => viewErrorDetails(error.id)}
                >
                  <td>
                    <span className={`severity-badge ${getSeverityClass(error.severity)}`}>
                      {getSeverityIcon(error.severity)} {error.severity}
                    </span>
                  </td>
                  <td className="error-type">{error.error_type}</td>
                  <td className="error-message">{error.error_message?.substring(0, 60)}...</td>
                  <td className="error-page">{error.page_url?.split('/').pop() || '-'}</td>
                  <td className="error-time">{formatDate(error.created_at)}</td>
                  <td>
                    <span className={`status-badge ${error.resolved ? 'resolved' : 'unresolved'}`}>
                      {error.resolved ? '✓ Resolved' : '○ Open'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-icon"
                        onClick={(e) => { e.stopPropagation(); viewErrorDetails(error.id); }}
                        title="View Details"
                      >
                        👁️
                      </button>
                      {!error.resolved && (
                        <button 
                          className="btn-icon resolve"
                          onClick={(e) => { e.stopPropagation(); handleResolve(error.id); }}
                          title="Mark Resolved"
                        >
                          ✓
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {errors.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">✨</span>
              <p>No errors found matching your filters</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              className="btn btn-secondary"
            >
              ← Previous
            </button>
            <span className="page-info">
              Page {pagination.page} of {pagination.pages}
            </span>
            <button 
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.pages}
              className="btn btn-secondary"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Error Detail Modal */}
      {selectedError && (
        <div className="error-modal-overlay" onClick={() => setSelectedError(null)}>
          <div className="error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <span className={`severity-badge ${getSeverityClass(selectedError.severity)}`}>
                  {getSeverityIcon(selectedError.severity)} {selectedError.severity}
                </span>
                Error Details
              </h2>
              <button className="modal-close" onClick={() => setSelectedError(null)}>×</button>
            </div>

            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Error Type</label>
                  <span>{selectedError.error_type}</span>
                </div>
                <div className="detail-item">
                  <label>Error Code</label>
                  <span>{selectedError.error_code || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Page URL</label>
                  <span>{selectedError.page_url || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>User Action</label>
                  <span>{selectedError.user_action || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>User</label>
                  <span>{selectedError.user_email || 'Anonymous'}</span>
                </div>
                <div className="detail-item">
                  <label>IP Address</label>
                  <span>{selectedError.ip_address || '-'}</span>
                </div>
                <div className="detail-item">
                  <label>Created</label>
                  <span>{formatDate(selectedError.created_at)}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <span className={`status-badge ${selectedError.resolved ? 'resolved' : 'unresolved'}`}>
                    {selectedError.resolved ? `Resolved by ${selectedError.resolved_by_name || 'Admin'}` : 'Unresolved'}
                  </span>
                </div>
              </div>

              <div className="detail-section">
                <label>Error Message</label>
                <div className="error-message-full">{selectedError.error_message}</div>
              </div>

              {selectedError.stack_trace && (
                <div className="detail-section">
                  <label>Stack Trace</label>
                  <pre className="stack-trace">{selectedError.stack_trace}</pre>
                </div>
              )}

              {selectedError.browser_info && (
                <div className="detail-section">
                  <label>Browser Info</label>
                  <pre className="browser-info">{JSON.stringify(selectedError.browser_info, null, 2)}</pre>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {!selectedError.resolved && (
                <button 
                  className="btn btn-primary"
                  onClick={() => handleResolve(selectedError.id)}
                >
                  ✓ Mark as Resolved
                </button>
              )}
              <button className="btn btn-secondary" onClick={() => setSelectedError(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminErrors;
